import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import useTrainingCourse from "../../Hooks/useTrainingCourse";
import DataLoader from "../../Components/DataLoader";
import SubmitLoader from "../../Components/SubmitLoader";
import AlertMessage from "../../Components/AlertMessage";
import Modal from "../../Components/Modal/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faUserPlus,
  faCheck,
  faBookOpen,
  faQuestionCircle,
  faCalendarAlt,
  faUsers,
  faPlayCircle,
  faList,
  faChevronDown,
  faChevronUp,
  faTimesCircle,
  faMapMarkerAlt,
  faMobileAlt,
  faEnvelope,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import noImageAvailable from "../../Assets/no-image-available.png";
import "./CourseDetail.css";

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const {
    currentCourse,
    userEnrollments,
    practicalProviders,
    courseLoading,
    enrollmentsLoading,
    competenciesLoading,
    practicalProvidersLoading,
    createEnrollmentLoading,
    createAssessmentLoading,
    courseError,
    competenciesError,
    createEnrollmentError,
    createAssessmentError,
    createEnrollmentResponse,
    createAssessmentResponse,
    fetchCourse,
    fetchUserEnrollments,
    fetchCompetencies,
    fetchPracticalProviders,
    enrollInCourse,
    startAssessment,
    isEnrolledInCourse,
    getCourseEnrollment,
    getModuleCompetencies,
  } = useTrainingCourse();

  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [expandedLocations, setExpandedLocations] = useState(new Set());
  const [expandedContacts, setExpandedContacts] = useState(new Set());

  // Utility function to get module cover image
  const getModuleCoverImage = (module) => {
    if (module.coverImageContent && module.coverImageExtension) {
      try {
        let base64String;

        // Handle different formats from .NET
        if (typeof module.coverImageContent === 'string') {
          // If it's already a base64 string, use it directly
          base64String = module.coverImageContent;
        } else if (module.coverImageContent instanceof Uint8Array) {
          // Convert Uint8Array to base64
          base64String = btoa(String.fromCharCode(...module.coverImageContent));
        } else if (Array.isArray(module.coverImageContent)) {
          // Convert regular array to base64
          base64String = btoa(String.fromCharCode(...new Uint8Array(module.coverImageContent)));
        } else {
          // console.warn('Unexpected coverImageContent format:', typeof module.coverImageContent);
          return { src: noImageAvailable, isPlaceholder: true };
        }

        // Handle extension format - remove leading dot if present
        const extension = module.coverImageExtension.startsWith('.') 
          ? module.coverImageExtension.substring(1) 
          : module.coverImageExtension;

        return { src: `data:image/${extension};base64,${base64String}`, isPlaceholder: false };
      } catch (error) {
        // console.error('Error converting module cover image:', error);
        return { src: noImageAvailable, isPlaceholder: true };
      }
    }
    return { src: noImageAvailable, isPlaceholder: true };
  };

  // React Hook Form setup for enrollment
  const {
    register: registerEnrollment,
    handleSubmit: handleEnrollmentSubmit,
    reset: resetEnrollment,
    formState: { errors: enrollmentErrors },
  } = useForm({
    defaultValues: {
      courseId: parseInt(courseId),
    },
  });

  // React Hook Form setup for assessment
  const {
    register: registerAssessment,
    handleSubmit: handleAssessmentSubmit,
    reset: resetAssessment,
    formState: { errors: assessmentErrors },
  } = useForm();

  useEffect(() => {
    if (courseId) {
      fetchCourse(parseInt(courseId));
      fetchCompetencies(); // Need to fetch competencies for getModuleCompetencies to work
      fetchUserEnrollments();
      fetchPracticalProviders(); // Fetch practical providers
    }
  }, [courseId]);

  useEffect(() => {
    if (createEnrollmentResponse) {
      setShowEnrollModal(false);
      resetEnrollment(); // Reset form after successful enrollment
      // Redirect to training courses list page
      navigate("/trainingcourses");
    }
  }, [createEnrollmentResponse, resetEnrollment, navigate]);

  useEffect(() => {
    if (createAssessmentResponse) {
      // Set competency assessment ID from response and redirect to assessment page
      const competencyAssessmentId = createAssessmentResponse;
      navigate(`/trainingcourses/assessment/${competencyAssessmentId}`);
    }
  }, [createAssessmentResponse, navigate]);

  useEffect(() => {
    // Reset form when modal opens
    if (showEnrollModal) {
      resetEnrollment({
        courseId: parseInt(courseId),
      });
    }
  }, [showEnrollModal, courseId, resetEnrollment]);

  // Get modules from course navigation property - now includes full Module objects
  const courseModules =
    currentCourse?.dlTrainingCourseModules?.sort(
      (a, b) => (a.moduleListOrder || 0) - (b.moduleListOrder || 0)
    ) || [];

  const isEnrolled = isEnrolledInCourse(parseInt(courseId));
  const enrollment = getCourseEnrollment(parseInt(courseId));

  // Check if course is completed (status is 'P')
  const isCompleted = enrollment?.courseAssessmentStatus === 'P';

  const handleEnrollSubmit = (data) => {
    enrollInCourse(data);
  };

  const handleModuleClick = (moduleId) => {
    if (isEnrolled) {
      setExpandedModules((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(moduleId)) {
          newSet.delete(moduleId);
        } else {
          newSet.add(moduleId);
        }
        return newSet;
      });
    }
  };

  const handleStartAssessment = (data) => {
    const assessmentData = {
      courseId: parseInt(courseId),
      competencyId: data.competencyId,
    };
    startAssessment(assessmentData);
  };

  // Helper function to convert status codes to readable text
  const getReadableStatus = (status) => {
    switch (status) {
      case "P":
        return "Passed";
      case "F":
        return "Failed";
      case "I":
        return "In Progress";
      case "N":
        return "Not Started";
      default:
        return "Active";
    }
  };

  const isLoading =
    courseLoading ||
    enrollmentsLoading ||
    competenciesLoading ||
    createAssessmentLoading;
  const hasError = courseError || competenciesError;

  if (isLoading) {
    return <SubmitLoader />;
  }

  if (hasError) {
    return (
      <AlertMessage
        message={courseError || competenciesError}
        type="error"
        className="course-detail-error"
      />
    );
  }

  if (!currentCourse) {
    return (
      <div className="course-not-found">
        <h2>Course Not Found</h2>
        <p>The requested course could not be found.</p>
        <button
          onClick={() => navigate("/trainingcourses")}
          className="btn-back"
        >
          Back to Courses
        </button>
      </div>
    );
  }

  return (
    <div className="course-detail-container">
      {/* Header */}
      <div className="course-detail-header">
        <button
          className="btn-back"
          onClick={() => navigate("/trainingcourses")}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Courses
        </button>

        <div className="course-info">
          <div className="course-header-content">
            <div className="course-code-badge">{currentCourse.courseCode}</div>
            <h1 className="course-title">{currentCourse.courseName}</h1>
            <p className="course-description">
              {currentCourse.courseDecription}
            </p>
          </div>

          <div className="course-actions">
            {isEnrolled ? (
              <>
              <div className="enrollment-status">
                <FontAwesomeIcon icon={faCheck} />
                <div className="enrollment-info">
                  <span className="enrollment-label">Enrolled</span>
                </div>
              </div>
              <div className="enrollment-status">
                <div className="enrollment-info">
                  {enrollment && (
                    <>
                      <span className="status-label">
                        <span className="status-text">Status:</span> {getReadableStatus(enrollment.courseAssessmentStatus)}
                      </span>
                      {enrollment.courseStartDate && (
                        <span className="started-label">
                          <span className="status-text">Due:</span> <span className="text-danger">{enrollment.courseCompleteByDate ? new Date(enrollment.courseCompleteByDate).toLocaleString() : "N/A"}</span>
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
              </>
            ) : (
              <button
                className="btn-enroll"
                onClick={() => setShowEnrollModal(true)}
              >
                <FontAwesomeIcon icon={faUserPlus} />
                Enroll Now
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Completion Page for Passed Status */}
      {isCompleted && (
        <div className="completion-page">
          <div className="completion-header">
            <div className="completion-icon">
              <FontAwesomeIcon icon={faCheck} />
            </div>
            <h2 className="completion-title">Congratulations!</h2>
            <p className="completion-subtitle">
              You have successfully completed the theoretical component of your boat licence course.
            </p>
          </div>

          <div className="mast-id-section">
            <div className="mast-id-card">
              <div className="mast-id-header">
                <FontAwesomeIcon icon={faUsers} />
                <h3>Your MAST ID</h3>
              </div>
              <div className="mast-id-content">
                <div className="mast-id-number">{currentCourse?.wwwId || 'Not Available'}</div>
                <p className="mast-id-description">
                  Use this MAST ID when enrolling in your practical boat licence course.
                </p>
              </div>
            </div>
          </div>

          <div className="providers-section">
            <div className="providers-header">
              <FontAwesomeIcon icon={faBookOpen} />
              <h3>Accredited Practical Course Providers</h3>
              <p>Choose from the following accredited providers to complete your practical training:</p>
            </div>

            <div className="providers-grid">
              {practicalProvidersLoading ? (
                <DataLoader />
              ) : practicalProviders && practicalProviders.length > 0 ? (
                practicalProviders.map((provider) => (
                  <div key={provider.cmpyId} className="provider-card">
                    <div className="provider-header">
                      <div className="provider-icon">
                        <FontAwesomeIcon icon={faUsers} />
                      </div>
                      <h4 className="provider-name">{provider.cmpyName}</h4>
                    </div>
                    
                    <div className="provider-details">
                      {provider.locations && provider.locations.length > 0 && (() => {
                        const isExpanded = expandedLocations.has(provider.cmpyId);
                        const displayLocations = isExpanded ? provider.locations : provider.locations.slice(0, 3);
                        const hasMore = provider.locations.length > 3;
                        
                        return (
                          <div className="provider-location-section">
                            <div className="location-header">
                              <FontAwesomeIcon icon={faMapMarkerAlt} />
                              <span>Locations</span>
                            </div>
                            <ul className="location-list">
                              {displayLocations.map((loc, idx) => (
                                <li key={idx}>
                                  {loc.locnPlace || loc.locnName}
                                  {loc.locnTown && `, ${loc.locnTown}`}
                                </li>
                              ))}
                            </ul>
                            {hasMore && (
                              <button 
                                className="btn-show-more"
                                onClick={() => {
                                  setExpandedLocations(prev => {
                                    const newSet = new Set(prev);
                                    if (newSet.has(provider.cmpyId)) {
                                      newSet.delete(provider.cmpyId);
                                    } else {
                                      newSet.add(provider.cmpyId);
                                    }
                                    return newSet;
                                  });
                                }}
                              >
                                {isExpanded ? 'Show Less' : `Show ${provider.locations.length - 3} More`}
                                <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                              </button>
                            )}
                          </div>
                        );
                      })()}
                      
                      <div className="provider-contact">
                        {provider.cmpyPhone && (
                          <div className="contact-item">
                            <FontAwesomeIcon icon={faPhone} />
                            <span>{provider.cmpyPhone}</span>
                          </div>
                        )}
                        {provider.cmpyEmail && (
                          <div className="contact-item">
                            <FontAwesomeIcon icon={faEnvelope} />
                            <span>{provider.cmpyEmail}</span>
                          </div>
                        )}
                      </div>
                      
                      {provider.courses && provider.courses.length > 0 && (() => {
                        const isContactsExpanded = expandedContacts.has(provider.cmpyId);
                        const displayContacts = isContactsExpanded ? provider.courses : provider.courses.slice(0, 3);
                        const hasMoreContacts = provider.courses.length > 3;
                        
                        return (
                          <div className="provider-personnel">
                            <h5 className="personnel-header">
                              <FontAwesomeIcon icon={faUsers} />
                              Training Contacts
                            </h5>
                            <div className="contact-cards-grid">
                              {displayContacts.map((contact, idx) => (
                                <div key={idx} className="contact-card">
                                  <div className="contact-avatar">
                                    {(contact.persFullName || contact.persFname || 'U').charAt(0).toUpperCase()}
                                  </div>
                                  <div className="contact-info">
                                    <div className="contact-name">
                                      {contact.persFullName || `${contact.persFname || ''} ${contact.persLname || ''}`.trim()}
                                    </div>
                                    {contact.persEmail && (
                                      <div className="contact-detail email">
                                        <FontAwesomeIcon icon={faEnvelope} />
                                        <span>{contact.persEmail}</span>
                                      </div>
                                    )}
                                    {contact.persMobile && (
                                      <div className="contact-detail phone">
                                        <FontAwesomeIcon icon={faMobileAlt} />
                                        <span>{contact.persMobile}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                            {hasMoreContacts && (
                              <button 
                                className="btn-show-more-contacts"
                                onClick={() => {
                                  setExpandedContacts(prev => {
                                    const newSet = new Set(prev);
                                    if (newSet.has(provider.cmpyId)) {
                                      newSet.delete(provider.cmpyId);
                                    } else {
                                      newSet.add(provider.cmpyId);
                                    }
                                    return newSet;
                                  });
                                }}
                              >
                                {isContactsExpanded ? 'Show Less Contacts' : `Show ${provider.courses.length - 3} More Contacts`}
                                <FontAwesomeIcon icon={isContactsExpanded ? faChevronUp : faChevronDown} />
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                ))
              ) : (
                <p>No practical course providers available at this time.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modules Section - Only show if not completed */}
      {!isCompleted && (
        <div className="modules-section">
        <div className="modules-header">
          <h2>Course Modules</h2>
          <div className="meta-item">
            <FontAwesomeIcon icon={faUsers} />
            <span>
              {courseModules.length} Module
              {courseModules.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {courseModules.length === 0 ? (
          <div className="no-modules">
            <FontAwesomeIcon icon={faBookOpen} />
            <p>No modules available for this course.</p>
          </div>
        ) : (
          <div className="modules-card-list">
            {courseModules.map((module) => {
              // Get competencies using the helper function
              const moduleCompetencies = getModuleCompetencies(module.moduleId);
              const isExpanded = expandedModules.has(module.moduleId);
              const coverImageData = getModuleCoverImage(module);

              return (
                <div key={module.moduleId} className="module-card-row">
                  <div
                    className={`module-card-header ${
                      module.isEnabledModule && isEnrolled && moduleCompetencies.length > 0
                        ? "clickable"
                        : "disabled"
                    }`}
                    onClick={() => {
                      if (module.isEnabledModule && isEnrolled && moduleCompetencies.length > 0) {
                        handleModuleClick(module.moduleId);
                      }
                    }}
                  >
                    <div className="module-card-image">
                      <img 
                        src={coverImageData.src}
                        alt={module.moduleName}
                        className="module-cover-image"
                        data-placeholder={coverImageData.isPlaceholder}
                        onError={(e) => {
                          e.target.src = noImageAvailable;
                          e.target.setAttribute('data-placeholder', 'true');
                        }}
                      />
                    </div>

                    <div className="module-card-content">
                      {/* Top row with module name, version badge, and competency count */}
                      <div className="module-header-row">
                        <div className="module-title-with-version">
                          <h3 className="module-name">
                            <span className="module-name-title">
                              {module.moduleName}
                            </span>
                            {/* <span className="module-version-badge">
                              v{module.moduleVersion}
                            </span> */}
                          </h3>
                        </div>
                        <span className="competency-count-badge">
                          <FontAwesomeIcon icon={faQuestionCircle} />
                          {moduleCompetencies.length} Competencies
                        </span>
                      </div>

                      {/* Module description */}
                      <div className="module-description-row">
                        <p className="module-description">
                          {module.moduleDescription ||
                            "No description available"}
                        </p>
                      </div>

                      {/* Bottom center - View/Hide Competencies button */}
                      <div className="module-bottom-section">
                        {isEnrolled ? (
                          moduleCompetencies.length > 0 ? (
                            <div className="module-expand-button">
                              <FontAwesomeIcon
                                icon={isExpanded ? faChevronUp : faChevronDown}
                                className="expand-icon"
                              />
                              <span className="expand-text">
                                {isExpanded ? "Hide" : "View"} Competencies
                              </span>
                            </div>
                          ) : null
                        ) : (
                          <div className="module-locked-status">
                            <FontAwesomeIcon icon={faUsers} />
                            <span>Enroll to Access</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Competencies */}
                  {isExpanded && isEnrolled && (
                    <div className="module-competencies-section">
                      {/* Display assessment error message */}
                      {createAssessmentError && (
                        <AlertMessage
                          message={
                            typeof createAssessmentError === "string"
                              ? createAssessmentError
                              : "Failed to start assessment. Please try again."
                          }
                          type="error"
                          className="assessment-error"
                        />
                      )}

                      {/* Display ModelState validation errors for assessment */}
                      {createAssessmentError &&
                        typeof createAssessmentError === "object" &&
                        createAssessmentError.errors && (
                          <div className="validation-errors">
                            {Object.entries(createAssessmentError.errors).map(
                              ([field, fieldErrors]) => (
                                <div
                                  key={field}
                                  className="field-validation-error"
                                >
                                  <strong>{field}:</strong>
                                  <ul>
                                    {fieldErrors.map((error, index) => (
                                      <li key={index}>{error}</li>
                                    ))}
                                  </ul>
                                </div>
                              )
                            )}
                          </div>
                        )}

                      <div className="competencies-grid">
                        {moduleCompetencies.map((competency) => {
                          // Determine competency status for styling
                          const getCompetencyStatusClass = (status) => {
                            if (!status || status === "N") return "";
                            if (status === "P")
                              return "competency-card-success";
                            if (status === "F" || status === "T") return "competency-card-fail";
                            return "";
                          };

                          const getStatusIcon = (status) => {
                            if (status === "P") return faCheck;
                            if (status === "F" || status === "T") return faTimesCircle;
                            return null;
                          };

                          const statusClass = getCompetencyStatusClass(
                            competency.competencyAssessmentStatus
                          );
                          const statusIcon = getStatusIcon(
                            competency.competencyAssessmentStatus
                          );

                          // Check if competency has been started (attempt number > 0)
                          const hasStarted =
                            competency.competencyAttemptNum &&
                            competency.competencyAttemptNum > 0;
                          const cardClass =
                            hasStarted && !statusClass
                              ? "competency-card-ongoing"
                              : statusClass;

                          return (
                            <div
                              key={competency.competencyId}
                              className={`competency-card ${cardClass}`}
                            >
                              <div className="competency-card-header">
                                <h4 className="competency-name">
                                  {competency.competencyName}
                                </h4>
                                <div className="competency-header-badges">
                                  <span className="competency-code-badge">
                                    {competency.competencyCode}
                                  </span>
                                  {statusIcon && (
                                    <span
                                      className={`competency-status-badge ${statusClass}`}
                                    >
                                      <FontAwesomeIcon icon={statusIcon} />
                                      {competency.competencyAssessmentStatus ===
                                      "P"
                                        ? "Pass"
                                        : "Fail"}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <p className="competency-description">
                                {competency.competencyDescription ||
                                  "No description available"}
                              </p>

                              <div className="competency-details">
                                {competency.minutesToComplete && (
                                  <div className="competency-detail-item">
                                    <FontAwesomeIcon icon={faCalendarAlt} />
                                    <span>
                                      {competency.minutesToComplete} minutes
                                    </span>
                                  </div>
                                )}

                                {competency.maxCompetencyAttempts && (
                                  <div className="competency-detail-item">
                                    <FontAwesomeIcon icon={faQuestionCircle} />
                                    <span>
                                      Max {competency.maxCompetencyAttempts}{" "}
                                      attempts
                                    </span>
                                  </div>
                                )}

                                <div className="competency-detail-item">
                                  <FontAwesomeIcon icon={faPlayCircle} />
                                  {competency.competencyAttemptNum !==
                                    undefined &&
                                  competency.competencyAttemptNum !== null ? (
                                    <span>
                                      Attempts:{" "}
                                      {competency.competencyAttemptNum}
                                    </span>
                                  ) : (
                                    <span>Attempts: 0</span>
                                  )}
                                </div>
                              </div>

                              {competency.isEnabledCompetency && competency.competencyAssessmentStatus !==
                                "P" && (
                                <button
                                  type="button"
                                  className="btn-start-assessment-card"
                                  disabled={createAssessmentLoading}
                                  onClick={() => handleStartAssessment({ competencyId: competency.competencyId })}
                                >
                                  <FontAwesomeIcon icon={faPlayCircle} />
                                  {createAssessmentLoading
                                    ? "Starting..."
                                    : (competency.competencyAssessmentStatus === "F" || competency.competencyAssessmentStatus === "T")
                                    ? "Restart Competency"
                                    : hasStarted && !statusIcon
                                    ? "Continue"
                                    : "Start Competency"}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Enrollment Modal */}
      {showEnrollModal && (
        <Modal
          setOpenModal={setShowEnrollModal}
          title="Enroll in Course"
          submitBtn={createEnrollmentLoading ? "Enrolling..." : "Enroll"}
          submitClkEvent={handleEnrollmentSubmit(handleEnrollSubmit)}
          cancelBtn="Cancel"
          titleSize="1.5rem"
          
        >
          <form className="enrollment-form">
            {/* Display general error message */}
            {createEnrollmentError && (
              <AlertMessage
                message={
                  typeof createEnrollmentError === "string"
                    ? createEnrollmentError
                    : "Please correct the validation errors below."
                }
                type="error"
                className="enrollment-error"
              />
            )}

            {/* Display ModelState validation errors */}
            {createEnrollmentError &&
              typeof createEnrollmentError === "object" &&
              createEnrollmentError.errors && (
                <div className="validation-errors">
                  {Object.entries(createEnrollmentError.errors).map(
                    ([field, fieldErrors]) => (
                      <div key={field} className="field-validation-error">
                        <strong>{field}:</strong>
                        <ul>
                          {fieldErrors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )
                  )}
                </div>
              )}

            {/* Hidden field for courseId */}
            <input
              type="hidden"
              {...registerEnrollment("courseId", {
                required: "Course ID is required",
              })}
            />
            {enrollmentErrors.courseId && (
              <span className="error-message">
                {enrollmentErrors.courseId.message}
              </span>
            )}
            <p>
              By enrolling in this course, you agree to complete all modules and
              assessments within the specified time frame.
            </p>

            {createEnrollmentLoading && (
              <div className="enrollment-loading">
                <DataLoader />
              </div>
            )}
          </form>
        </Modal>
      )}
    </div>
  );
};

export default CourseDetail;
